"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { Image as GalleryImage } from "@/types";
export type PhotowallArrivalStyle = "off" | "polaroid" | "bubble";
export type PhotowallArrivalDirection = "bottom-to-top" | "top-to-bottom" | "left-to-right" | "right-to-left";
export interface PhotowallFlight {
  id: number;
  url: string;
  sequence: number;
  /** 0 for a lone photo, rising to 1 during a burst of uploads. */
  energy: number;
  delay?: number;
}
interface UsePhotowallArrivalsOptions {
  images: GalleryImage[];
  isActive: boolean;
  isPlaying: boolean;
  isEnabled: boolean;
  shouldAnnounce: boolean;
}
interface ReadyArrival {
  image: GalleryImage;
  url: string;
}
interface LaunchRhythm {
  jitter: number;
  isCluster: boolean;
  clusterGap: number;
}
const MAX_PENDING_ARRIVALS = 60;
const MAX_READY_ARRIVALS = 4;
const IMAGE_LOAD_TIMEOUT_MS = 5000;
// Uploads from one phone land a few seconds apart, so a burst is measured
// over a window rather than by how many photos wait at one moment.
const BURST_WINDOW_MS = 30000;
const ENERGY_RISE_MS = 1500;
const ENERGY_FALL_MS = 9000;
const CALM_LAUNCH_GAP_MS = 2800;
const BURST_LAUNCH_GAP_MS = 300;
const REDUCED_MOTION_GAP_MS = 600;
const POLL_MS = 400;
// Longer than any flight, so a card whose finish event never fires (a page
// that stops rendering frames, say) can't block every later arrival.
const MAX_FLIGHT_MS = 30000;
export function isPhotowallArrivalStyle(value: string | null): value is PhotowallArrivalStyle {
  return value === "off" || value === "polaroid" || value === "bubble";
}
export function isPhotowallArrivalDirection(value: string | null): value is PhotowallArrivalDirection {
  return value === "bottom-to-top" || value === "top-to-bottom" || value === "left-to-right" || value === "right-to-left";
}

/** Energy rises with recent uploads and the backlog waiting to be shown. */
function getTargetEnergy(recentCount: number, backlog: number): number {
  return Math.min(1, Math.max(0, (recentCount - 2) / 10, (backlog - 1) / 8));
}
function getMaxVisible(energy: number, isReducedMotion: boolean): number {
  return isReducedMotion ? 1 : Math.round(2 + energy * 7);
}
function createLaunchRhythm(): LaunchRhythm {
  return {
    jitter: 0.65 + Math.random() * 0.7,
    isCluster: Math.random() < 0.25,
    clusterGap: 120 + Math.random() * 120
  };
}

/** Lone photos keep a few seconds apart; bursts launch in quick, uneven clusters. */
function getLaunchGap(energy: number, rhythm: LaunchRhythm, isReducedMotion: boolean): number {
  if (isReducedMotion) return REDUCED_MOTION_GAP_MS;
  if (rhythm.isCluster && energy > 0.55) return rhythm.clusterGap;
  return (CALM_LAUNCH_GAP_MS + (BURST_LAUNCH_GAP_MS - CALM_LAUNCH_GAP_MS) * energy) * rhythm.jitter;
}
function getImageUrls(image: GalleryImage): string[] {
  return Array.from(new Set([image.thumbnail_url, image.optimized_url, image.s3URL].filter((url): url is string => Boolean(url))));
}
export function usePhotowallArrivals({
  images,
  isActive,
  isPlaying,
  isEnabled,
  shouldAnnounce
}: UsePhotowallArrivalsOptions) {
  const [flights, setFlights] = useState<PhotowallFlight[]>([]);
  const [isReducedMotion, setIsReducedMotion] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(true);
  const flightsRef = useRef<PhotowallFlight[]>([]);
  const pendingRef = useRef<GalleryImage[]>([]);
  const readyRef = useRef<ReadyArrival[]>([]);
  const imageByIdRef = useRef(new Map(images.map(image => [image.id, image])));
  const seenIdsRef = useRef(new Set(images.map(image_0 => image_0.id)));
  const sequenceRef = useRef(0);
  const arrivalTimesRef = useRef<number[]>([]);
  const energyRef = useRef({
    value: 0,
    updatedAt: 0
  });
  const lastLaunchAtRef = useRef(-Infinity);
  const rhythmRef = useRef<LaunchRhythm>(createLaunchRhythm());
  const wakeRef = useRef<(() => void) | undefined>(undefined);
  const launchedAtRef = useRef(new Map<number, number>());
  const replaceFlights = useCallback((nextFlights: PhotowallFlight[]): void => {
    const ids = new Set(nextFlights.map(flight => flight.id));
    launchedAtRef.current.forEach((_, id) => {
      if (!ids.has(id)) launchedAtRef.current.delete(id);
    });
    flightsRef.current = nextFlights;
    setFlights(nextFlights);
  }, []);
  const removeFlight = useCallback((id_0: number): void => {
    replaceFlights(flightsRef.current.filter(flight_0 => flight_0.id !== id_0));
    wakeRef.current?.();
  }, [replaceFlights]);
  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotion = (): void => setIsReducedMotion(media.matches);
    updateMotion();
    media.addEventListener("change", updateMotion);
    return () => media.removeEventListener("change", updateMotion);
  }, [isPlaying]);
  useEffect(() => {
    const updateVisibility = (): void => {
      setIsPageVisible(document.visibilityState === "visible");
    };
    updateVisibility();
    document.addEventListener("visibilitychange", updateVisibility);
    return () => document.removeEventListener("visibilitychange", updateVisibility);
  }, []);
  useEffect(() => {
    imageByIdRef.current = new Map(images.map(image_1 => [image_1.id, image_1]));
    const additions = images.filter(image_2 => !seenIdsRef.current.has(image_2.id));
    additions.forEach(image_3 => seenIdsRef.current.add(image_3.id));

    // Keep observing while stopped or disabled so starting never replays history.
    if (!isActive || !isEnabled) {
      pendingRef.current = [];
      readyRef.current = [];
      arrivalTimesRef.current = [];
      if (flightsRef.current.length > 0) replaceFlights([]);
      return;
    }
    const announced = shouldAnnounce ? additions : [];
    const now = performance.now();
    announced.forEach(() => arrivalTimesRef.current.push(now));
    pendingRef.current = [...pendingRef.current.filter(image_4 => imageByIdRef.current.has(image_4.id)), ...announced].slice(-MAX_PENDING_ARRIVALS);
    readyRef.current = readyRef.current.filter(ready => imageByIdRef.current.has(ready.image.id));
    const retainedFlights = flightsRef.current.filter(flight_1 => imageByIdRef.current.has(flight_1.id));
    if (retainedFlights.length !== flightsRef.current.length) {
      replaceFlights(retainedFlights);
    }
    if (announced.length > 0) wakeRef.current?.();
  }, [images, isActive, isEnabled, shouldAnnounce, replaceFlights]);
  useEffect(() => {
    if (isReducedMotion && flightsRef.current.length > 1) {
      replaceFlights(flightsRef.current.slice(-1));
    }
  }, [isReducedMotion, replaceFlights]);
  useEffect(() => {
    if (!isPlaying || !isEnabled) return;
    let isCancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const loads = new Map<number, {
      image: GalleryImage;
      cancel: () => void;
    }>();
    const updateEnergy = (now_0: number): number => {
      arrivalTimesRef.current = arrivalTimesRef.current.filter(time => now_0 - time < BURST_WINDOW_MS);
      const backlog = pendingRef.current.length + readyRef.current.length + loads.size;
      const target = getTargetEnergy(arrivalTimesRef.current.length, backlog);
      const energy = energyRef.current;
      const timeConstant = target > energy.value ? ENERGY_RISE_MS : ENERGY_FALL_MS;
      energy.value += (target - energy.value) * (1 - Math.exp(-(now_0 - energy.updatedAt) / timeConstant));
      energy.updatedAt = now_0;
      return energy.value;
    };
    const startLoad = (image_5: GalleryImage): void => {
      const urls = getImageUrls(image_5);
      let urlIndex = 0;
      let probe: HTMLImageElement | undefined;
      let timeout: ReturnType<typeof setTimeout> | undefined;
      const release = (): void => {
        clearTimeout(timeout);
        if (probe) {
          probe.onload = null;
          probe.onerror = null;
        }
      };
      const tryNextUrl = (): void => {
        release();
        if (probe) probe.src = "";
        const url = urls[urlIndex++];
        if (isCancelled) return;
        if (!url || !imageByIdRef.current.has(image_5.id)) {
          loads.delete(image_5.id);
          tick();
          return;
        }
        const nextProbe = new window.Image();
        probe = nextProbe;
        timeout = setTimeout(tryNextUrl, IMAGE_LOAD_TIMEOUT_MS);
        nextProbe.onerror = tryNextUrl;
        nextProbe.onload = (): void => {
          release();
          loads.delete(image_5.id);
          if (isCancelled || !imageByIdRef.current.has(image_5.id)) return;
          readyRef.current.push({
            image: image_5,
            url
          });
          tick();
        };
        nextProbe.src = url;
      };
      loads.set(image_5.id, {
        image: image_5,
        cancel: () => {
          release();
          if (probe) probe.src = "";
        }
      });
      tryNextUrl();
    };
    function tick(): void {
      if (isCancelled) return;
      clearTimeout(timer);
      timer = undefined;
      const now_1 = performance.now();
      const energy_0 = updateEnergy(now_1);
      const isVisible = document.visibilityState === "visible";
      pendingRef.current = pendingRef.current.filter(image_6 => imageByIdRef.current.has(image_6.id));
      readyRef.current = readyRef.current.filter(ready_0 => imageByIdRef.current.has(ready_0.image.id));
      const liveFlights = flightsRef.current.filter(flight_2 => now_1 - (launchedAtRef.current.get(flight_2.id) ?? now_1) < MAX_FLIGHT_MS);
      if (liveFlights.length !== flightsRef.current.length) replaceFlights(liveFlights);

      // Preload ahead so a burst isn't held back by one slow photo.
      const concurrency = isReducedMotion ? 1 : 1 + Math.round(energy_0 * 2);
      while (isVisible && loads.size < concurrency && readyRef.current.length + loads.size < MAX_READY_ARRIVALS && pendingRef.current.length > 0) {
        const nextImage = pendingRef.current.shift();
        if (nextImage) startLoad(nextImage);
      }
      let waitMs = lastLaunchAtRef.current + getLaunchGap(energy_0, rhythmRef.current, isReducedMotion) - now_1;
      const next = readyRef.current[0];
      if (isVisible && next && flightsRef.current.length < getMaxVisible(energy_0, isReducedMotion) && waitMs <= 0) {
        readyRef.current.shift();
        launchedAtRef.current.set(next.image.id, now_1);
        replaceFlights([...flightsRef.current, {
          id: next.image.id,
          url: next.url,
          sequence: sequenceRef.current++,
          energy: energy_0
        }]);
        lastLaunchAtRef.current = now_1;
        rhythmRef.current = createLaunchRhythm();
        waitMs = getLaunchGap(energy_0, rhythmRef.current, isReducedMotion);
      }

      // Loads and finished cards wake the queue themselves; the poll only
      // follows the launch rhythm and changes in visibility or energy.
      const hasBacklog = pendingRef.current.length > 0 || readyRef.current.length > 0 || loads.size > 0;
      if (hasBacklog) {
        const isGapPending = readyRef.current.length > 0 && waitMs > 0;
        timer = setTimeout(tick, isGapPending ? Math.max(16, Math.min(POLL_MS, waitMs)) : POLL_MS);
      }
    }
    wakeRef.current = tick;
    tick();
    return () => {
      isCancelled = true;
      wakeRef.current = undefined;
      clearTimeout(timer);
      // Photos still loading go back to the front of the queue for a restart.
      const interrupted = Array.from(loads.values());
      interrupted.forEach(load => load.cancel());
      pendingRef.current = [...interrupted.map(load_0 => load_0.image), ...pendingRef.current].slice(0, MAX_PENDING_ARRIVALS);
      loads.clear();
    };
  }, [isPlaying, isEnabled, isReducedMotion, replaceFlights]);
  return {
    flights,
    removeFlight,
    isReducedMotion,
    isPageVisible
  };
}